import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { DialogService } from '@services/dialog.service';
import { View } from '@shared/components/data-view/data-view-types';
import { DataViewGalleryComponent } from '@shared/components/data-view/view-gallery/data-view-gallery.component';
import { DataViewState, ViewPreset } from '@shared/dataview/core/dataview-types';
import { DataViewPageStore } from '@shared/dataview/state/page.store';
import { ViewPresetStore } from '@shared/dataview/state/view-preset.store';
import { isEqual } from 'lodash';

@Component({
  selector: 'dv-view-tabs',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ul class="nav nav-tabs dv-view-tabs" role="tablist" data-testid="dv-view-tabs">
      @for (view of views(); track view.id) {
        <li class="nav-item" role="presentation">
          <button
            type="button"
            role="tab"
            class="nav-link dv-view-tabs__tab"
            [class.active]="view.id === currentId() && !isGalleryOpen()"
            [class.dv-view-tabs__tab--active]="view.id === currentId()"
            [attr.aria-selected]="view.id === currentId()"
            [attr.data-testid]="'dv-view-tab-' + view.id"
            (click)="store.select(view.id)"
          >
            {{ view.label }}
            @if (view.id === currentId() && hasUnsavedChanges()) {
              <span class="dv-view-tabs__dirty" data-testid="dv-view-tab-unsaved" aria-label="Unsaved changes">*</span>
            }
          </button>
        </li>
      } @empty {
        <li class="nav-item" role="presentation">
          <span class="nav-link active" [class.placeholder-glow]="isLoading()" data-testid="dv-view-tabs-placeholder">
            @if (isLoading()) {
              <span class="placeholder dv-view-tabs__placeholder"></span>
            } @else {
              No views found
            }
          </span>
        </li>
      }

      @if (store.views().length > 0) {
        <li class="nav-item" role="presentation">
          <button
            type="button"
            class="nav-link dv-view-tabs__tab"
            data-testid="dv-view-tabs-all"
            [class.active]="isGalleryOpen()"
            (click)="openGallery()"
          >
            All Views
          </button>
        </li>
      }
    </ul>
  `,
  styleUrl: './dv-view-tabs.component.scss',
})
export class DvViewTabsComponent {
  private readonly dialogService = inject(DialogService);
  private readonly page = inject<DataViewPageStore<unknown>>(DataViewPageStore, { optional: true });

  protected readonly store: ViewPresetStore<DataViewState> = this.page?.views ?? inject<ViewPresetStore<DataViewState>>(ViewPresetStore);

  protected readonly views = computed(() => this.store.pinnedViews());
  protected readonly currentId = computed(() => this.store.currentId());
  protected readonly hasUnsavedChanges = computed(() => this.store.hasUnsavedChanges());
  protected readonly isLoading = computed(() => this.page?.isLoadingConfig() ?? true);
  protected readonly isGalleryOpen = signal(false);

  protected openGallery(): void {
    if (this.isGalleryOpen()) return;
    this.isGalleryOpen.set(true);

    const slideout = this.dialogService.openSlideout(DataViewGalleryComponent, { position: 'end', panelClass: 'editColumnsPanel' });
    const gallery = slideout.componentInstance;
    const before = this.store.views().map(toGalleryView);
    gallery.views.set(before);
    gallery.canShareViews.set(false);

    slideout.hidden.subscribe(() => {
      this.isGalleryOpen.set(false);
      if (isEqual(before, gallery.views())) return;

      this.store.rearrange(gallery.views());
      this.page?.savePreferences();
    });
  }
}

function toGalleryView({ id, label, isPinned, isCustom, ownerUsername }: ViewPreset<DataViewState>): View.UserView & View.DefaultView {
  return { id, label, isPinned, isCustom, ownerUsername } as View.UserView & View.DefaultView;
}
